import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, X, BadgeCheck } from "lucide-react";
import api from "@/lib/api";

const INTERVAL = 30000; // 30 seconds
const VISIBLE = 7000; // show each for 7s

export default function ReviewPopup() {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(null);
  const idxRef = useRef(0);
  const hideTimer = useRef(null);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!items.length) return;
    const show = () => {
      const item = items[idxRef.current % items.length];
      idxRef.current += 1;
      setCurrent(item);
      clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => setCurrent(null), VISIBLE);
    };
    const first = setTimeout(show, 8000); // first popup after 8s
    const loop = setInterval(show, INTERVAL);
    return () => { clearTimeout(first); clearInterval(loop); clearTimeout(hideTimer.current); };
  }, [items]);

  const close = () => { clearTimeout(hideTimer.current); setCurrent(null); };

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          key={current.id}
          data-testid="review-popup"
          initial={{ opacity: 0, x: -40, y: 20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ type: "spring", damping: 22, stiffness: 240 }}
          className="fixed z-[65] bottom-6 left-6 w-[300px] max-w-[calc(100vw-3rem)] bg-heirloom-bone rounded-2xl shadow-[0_20px_50px_-20px_rgba(0,0,0,0.45)] border border-heirloom-line p-4"
        >
          <button data-testid="review-popup-close" onClick={close} className="absolute top-2.5 right-2.5 text-heirloom-obsidian/35 hover:text-heirloom-maroon transition-colors">
            <X size={16} />
          </button>
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-full bg-heirloom-maroon text-heirloom-bone flex items-center justify-center font-display text-lg">
              {current.name?.charAt(0) || "A"}
            </span>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-heirloom-obsidian flex items-center gap-1">
                {current.name} <BadgeCheck size={14} className="text-heirloom-gold" />
              </div>
              <div className="overline text-[0.5rem] text-heirloom-maroon">{current.event}</div>
            </div>
          </div>
          <div className="flex gap-0.5 mt-2.5">
            {[...Array(5)].map((_, i) => <Star key={i} size={12} className="fill-heirloom-gold text-heirloom-gold" />)}
          </div>
          <p className="text-xs text-heirloom-obsidian/70 mt-2 leading-relaxed line-clamp-3">
            &ldquo;{current.quote}&rdquo;
          </p>
          <div className="overline text-[0.5rem] text-heirloom-obsidian/40 mt-3">Verified Client Review</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
